/**
 * 템플릿 고르기 팝업 — 갤러리. 2026-09-30.
 *
 * 카드마다 그 템플릿의 실제 화면 모양을 작게 보여 준다 — 그림이 아니라 공용 페이지(LayoutRuntime)를
 * 진짜 SearchPanel · SnForm · SnGrid · SnTab 으로 그린 뒤 축소한 것이다. 카드를 누르면 크게 보이고, 거기서 고른다.
 * 조회조건 · 폼 · 그리드 칼럼은 본보기 값(거래처 · 품목 · 검색 / 번호 · 일자 · …)이다. 템플릿은 배치만 정한다.
 * showPopupModal 의 규약대로 getSelectedRow 로 고른 것을 돌려준다.
 */
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DefRow, DesignTemplate, fetchDesignLayout, fetchDesignTemplates } from "@shared/api/designApi";
import { buildLayoutTree, gridIdsOf, LayoutRuntime } from "@menu/COM/SN_COM_LAYOUT/SN_COM_LAYOUT";
import SnButton from "@shared/components/SnButton";

interface Props {
  fgMenu?: string; // REG · REP 로 좁힌다. 비면 전부
  modalRef?: any;
}

/** 본보기 조회조건 · 폼 — sys_formctrl 행 모양 (08_AI_화면설정.sql 의 줄과 같은 칸). 폼은 12칸 그리드에 세 줄 */
function ctrl(cdForm: string, nmLabel: string, cdParam: string, row: number, col: number, span: number, seq: number): DefRow {
  return {
    cd_form: cdForm, no_line: seq, tp_ctrl: "SnTextBox", qt_rowindex: row, qt_rowspan: 1, qt_colindex: col, qt_colspan: span,
    nm_label: nmLabel, cd_param: cdParam, fg_visible: "Y", yn_required: "N", no_seq: seq,
  };
}
const SAMPLE_CONTROLS: DefRow[] = [
  ctrl("search", "거래처", "cd_cust", 1, 1, 3, 1),
  ctrl("search", "품목", "cd_item", 1, 4, 3, 2),
  ctrl("search", "검색", "search", 1, 7, 6, 3),
  ctrl("freeform1", "번호", "no_doc", 1, 1, 6, 1),
  ctrl("freeform1", "일자", "dt_doc", 1, 7, 6, 2),
  ctrl("freeform1", "거래처", "nm_cust", 2, 1, 6, 3),
  ctrl("freeform1", "품목", "nm_item", 2, 7, 6, 4),
  ctrl("freeform1", "금액", "am_total", 3, 1, 6, 5),
  ctrl("freeform1", "비고", "dc_rmk", 3, 7, 6, 6),
];

/** 본보기 그리드 칼럼 — sys_formgridcolumn 행 모양 */
function col(cdGrid: string, cdColumn: string, nmColumn: string, width: number, seq: number, tp = "SnTextBox", format?: string): DefRow {
  return {
    cd_grid: cdGrid, cd_column: cdColumn, nm_column: nmColumn, tp_column: tp, fg_visible: "Y", no_seq: seq, qt_width: width,
    yn_required: "N", yn_allowsorting: "Y", yn_fixed: "N", qt_groupindex: -1, yn_sum: "N", yn_system: "N", yn_drilldown: "N",
    ...(format ? { tp_format: format } : {}),
  };
}
function sampleColumns(cdGrid: string): DefRow[] {
  return [
    col(cdGrid, "no_doc", "번호", 110, 0),
    col(cdGrid, "dt_doc", "일자", 100, 1),
    col(cdGrid, "nm_cust", "거래처", 160, 2),
    col(cdGrid, "nm_item", "품목", 160, 3),
    col(cdGrid, "fg_status", "상태", 80, 4),
    col(cdGrid, "am_total", "금액", 110, 5, "SnNumberBox", "#,##0"),
  ];
}
function sampleGrids(layout: DefRow[]): Record<string, DefRow[]> {
  const out: Record<string, DefRow[]> = {};
  gridIdsOf(buildLayoutTree(layout)).forEach((id) => {
    out[id] = sampleColumns(id);
  });
  return out;
}

/** 실제 화면을 그리는 기준 크기. 카드는 이것을 카드 폭에 맞게 축소한다 */
const BASE_W = 1100;
const BASE_H = 520;

/** 축소 미리보기 — 진짜 컴포넌트를 기준 크기로 그린 뒤 transform 으로 줄인다. 누르는 것은 카드가 받는다 */
function Thumb({ cdMenu, layout }: { cdMenu: string; layout: DefRow[] }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.3);
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const fit = () => setScale(Math.max(0.1, el.clientWidth / BASE_W));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const grids = useMemo(() => sampleGrids(layout), [layout]);
  return (
    <div ref={boxRef} className="w-full overflow-hidden bg-white pointer-events-none select-none" style={{ height: BASE_H * scale }}>
      <div style={{ width: BASE_W, height: BASE_H, transform: `scale(${scale})`, transformOrigin: "top left" }}>
        <LayoutRuntime
          cdMenu={`TPLTHUMB_${cdMenu}`}
          layout={layout}
          formData={{}}
          formControlData={SAMPLE_CONTROLS}
          grids={grids}
          onSearch={async () => undefined}
        />
      </div>
    </div>
  );
}

const TemplatePicker = forwardRef(({ fgMenu, modalRef }: Props, ref) => {
  const [templates, setTemplates] = useState<DesignTemplate[]>([]);
  const [layouts, setLayouts] = useState<Record<string, DefRow[]>>({});
  const [selected, setSelected] = useState<DesignTemplate | null>(null);
  const [zoomed, setZoomed] = useState<DesignTemplate | null>(null);
  /* 팝업이 getSelectedRow 를 바로 읽으므로 state 반영을 기다리지 않게 ref 로도 든다 */
  const selectedRef = useRef<DesignTemplate | null>(null);
  const choose = (t: DesignTemplate) => {
    selectedRef.current = t;
    setSelected(t);
  };

  useEffect(() => {
    (async () => {
      try {
        const list = (await fetchDesignTemplates()).filter((t) => !fgMenu || t.fg_menu === fgMenu);
        setTemplates(list);
        const rows = await Promise.all(list.map((t) => fetchDesignLayout(t.cd_menu).catch(() => [] as DefRow[])));
        const all: Record<string, DefRow[]> = {};
        list.forEach((t, i) => {
          all[t.cd_menu] = rows[i];
        });
        setLayouts(all);
      } catch (e) {}
    })();
  }, [fgMenu]);

  useImperativeHandle(ref, () => ({
    getSelectedRow: () => (selectedRef.current ? [selectedRef.current] : []),
  }));

  const zoomGrids = useMemo(() => (zoomed ? sampleGrids(layouts[zoomed.cd_menu] || []) : {}), [zoomed, layouts]);

  /* 크게 보기 — 갤러리 대신 그 템플릿 하나를 팝업 전체에. [이 모양으로] 는 팝업의 확인과 같다 */
  if (zoomed) {
    return (
      <div className="w-full h-full min-h-0 flex flex-col">
        <div className="flex items-center justify-between px-1 pb-2 border-b">
          <div className="text-sm">
            <span className="font-semibold">{zoomed.nm_title}</span>
            <span className="ml-2 text-gray-500 font-mono text-xs">{zoomed.cd_menu}</span>
          </div>
          <div className="flex space-x-2">
            <SnButton
              text="이 모양으로"
              onClick={() => {
                choose(zoomed);
                modalRef?.current?.handleConfirm?.(true);
              }}
            />
            <SnButton text="목록" onClick={() => setZoomed(null)} />
          </div>
        </div>
        <div className="flex-1 min-h-0 pt-1">
          <LayoutRuntime
            key={zoomed.cd_menu}
            cdMenu={`TPLPREVIEW_${zoomed.cd_menu}`}
            layout={layouts[zoomed.cd_menu] || []}
            formData={{}}
            formControlData={SAMPLE_CONTROLS}
            grids={zoomGrids}
            onSearch={async () => undefined}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full min-h-0 overflow-auto">
      <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 p-1">
        {templates.map((t) => {
          const on = selected?.cd_menu === t.cd_menu;
          return (
            <div
              key={t.cd_menu}
              onClick={() => {
                choose(t);
                setZoomed(t);
              }}
              className={`group cursor-pointer rounded-xl border bg-white overflow-hidden transition-shadow hover:shadow-lg ${
                on ? "border-blue-500 ring-2 ring-blue-200" : "border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-sm font-semibold truncate">{t.nm_title}</span>
                <span className={`text-[11px] px-1.5 py-0.5 rounded ${t.fg_menu === "REP" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                  {t.fg_menu === "REP" ? "현황" : "등록"}
                </span>
              </div>
              <div className="border-t border-gray-100 bg-gray-50">
                {layouts[t.cd_menu] ? <Thumb cdMenu={t.cd_menu} layout={layouts[t.cd_menu]} /> : <div className="h-40" />}
              </div>
              <div className="px-3 py-1.5 text-[11px] text-gray-500 flex justify-between">
                <span className="font-mono">{t.cd_menu}</span>
                <span className="opacity-0 group-hover:opacity-100 transition-opacity">크게 보기</span>
              </div>
            </div>
          );
        })}
        {templates.length === 0 && <div className="col-span-3 text-gray-500 text-sm p-2">템플릿이 없습니다. 템플릿 스크립트를 먼저 돌려 주세요.</div>}
      </div>
    </div>
  );
});

export default TemplatePicker;
